import './ReplaceDialog.css';
import * as React from 'react';
import { Dictionary, IPatch, UpdateOptions } from '../../../entities/common';
import { Attachment, IAttachmentActions, IAttachmentAttrs } from '../../../entities/Subentities';
import { nameof } from '../../../store/services/metadataService';
import { ConfirmationDialog } from '../../common/ConfirmationDialog';
import { ICreationData } from '../../../store/Subentity';
import { DialogType } from 'office-ui-fabric-react';

type ReplaceDialogProps = {
    attachments: Attachment[];
    data: ReplaceData;
    actions: IAttachmentActions;
    onDismiss: () => void;
}

const ReplaceDialog = (props: React.PropsWithChildren<ReplaceDialogProps>) => {
    const { attachments, data, actions, onDismiss } = props;
    const isMultiple = data.items.length > 1;
    const content = <div className='replace-attachments-dialog'>
        {
            isMultiple
                ? <>
                    <ul>Attachments with the following names already exist:
                        {Array.from<WithDuplicate>(data.items)
                            .filter((_: WithDuplicate) => _.duplicateId)
                            .map(_ => <li>"{_.attributes.Name}"</li>)}
                    </ul>
                    Do you want to replace them or keep all attachments?
                </>
                : `An attachment with the name "${data.items[0].attributes.Name}" already exists. Do you want to replace it or keep both attachments?`
        }
    </div>;

    const onKeepBoth = () => {
        switch (data.action) {
            case "Create":
                actions.create?.(makeUniqueName(data.items, attachments)[0]);
                break;
            case "Update":
                actions.bulkUpdate?.(makeUniqueName(data.items, attachments));
                break;
            case "Upload": {
                const files = makeUniqueName(data.items, attachments);
                actions.uploadFile(files.map(_ => ({ file: new File([_.file], _.attributes.Name, { type: _.file.type }) })));
                break;
            }
        }

        onDismiss();
    }

    const onReplace = () => {
        switch (data.action) {
            case "Create":
            case "Update": {
                const item = data.items[0];
                actions.replace?.(item, item.duplicateId!);
                break;
            }
            case "Upload":
                actions.uploadFile(data.items.map(_ => ({ file: _.file, id: _.duplicateId })));
                break;
        }

        onDismiss();
    }

    const handleDismiss = React.useCallback((ev?: React.MouseEvent<HTMLButtonElement>) => {
        if (ev && (ev as any).key === 'Escape') {
            ev.preventDefault();
            return;
        }

        onDismiss();
    }, []);

    return (
        <ConfirmationDialog
            onDismiss={handleDismiss}
            onYes={onReplace}
            onNo={onKeepBoth}
            yesButtonProps={{ text: isMultiple ? "Replace All" : "Replace" }}
            noButtonProps={{ text: isMultiple ? "Keep All" : "Keep both" }}
            dialogContentProps={{
                title: isMultiple
                    ? 'Attachments With Such Names Already Exist'
                    : 'Attachment With Such Name Already Exists',
                showCloseButton: false,
                type: DialogType.normal
            }}
            modalProps={{
                isBlocking: true
            }}
        >
            {content}
        </ConfirmationDialog>
    );
}

type WithDuplicate = { duplicateId?: string, attributes: Dictionary<any> };
type ReplaceData = { action: "Create", items: (ICreationData<IAttachmentAttrs> & WithDuplicate)[] }
    | { action: "Update", items: (IPatch<Attachment> & WithDuplicate)[] }
    | { action: "Upload", items: ({ file: File; } & WithDuplicate)[] };

const buildActionsWithReplace = <TActions extends IAttachmentActions,>(
    items: Attachment[],
    actions: TActions,
    setReplaceData: (data: ReplaceData | undefined) => void
) => ({
    ...actions,
    create: (item: ICreationData<IAttachmentAttrs>) => {
        const duplicate = items.find(_ => _.attributes.Name === item.attributes.Name);
        if (duplicate) {
            setReplaceData({ items: [{ duplicateId: duplicate.id, ...item }], action: "Create" });
        } else {
            actions.create?.(item);
        }
    },
    update: (id: string, changes: Dictionary<unknown>, options?: UpdateOptions) => {
        const newName = changes[nameof<IAttachmentAttrs>("Name")];
        if (!newName) {
            actions.update?.(id, changes, options);
            return;
        }

        const item = items.find(_ => _.id === id);
        if (!item) {
            return;
        }

        const duplicate = items.find(_ => _.id !== id && _.attributes.Name === newName);
        if (!duplicate) {

            actions.update?.(id, changes, options);
            return;
        }

        const replaceData = {
            id: id,
            attributes: changes,
            duplicateId: duplicate.id
        };

        setReplaceData({
            items: [replaceData],
            action: "Update"
        });
    },
    uploadFile: (files: File[]) => {
        const withDuplicates = files.map(file => ({
            file: file,
            duplicate: items.find(_ => _.attributes.Name === file.name)
        }))

        if (withDuplicates.some(_ => _.duplicate)) {
            const replaceItems = withDuplicates.map(_ => ({
                duplicateId: _.duplicate?.id,
                attributes: { Name: _.file.name },
                file: _.file
            }));
            
            setReplaceData({ items: replaceItems, action: "Upload" });
        } else {
            actions.uploadFile?.(files.map(_ => ({ file: _ })));
        }
    }
});

const useActionsWithReplace = <TActions extends IAttachmentActions,>(items: Attachment[], actions?: TActions) => {
    const [replaceData, setReplaceData] = React.useState<ReplaceData>();

    return {
        replaceData,
        setReplaceData,
        actions: actions
            ? buildActionsWithReplace(items, actions, setReplaceData)
            : undefined
    }
}

type TBaseItem = { attributes: { Name?: string } };
type Comparer<TItem,> = (item: TItem, attachment: Attachment) => boolean;
const defaultComparer: Comparer<TBaseItem> = (item, _) => extractNameAndExtension(_.attributes.Name).name === extractNameAndExtension(item.attributes.Name).name;

const makeUniqueName = <TItem extends TBaseItem>(items: TItem[], attachments: Attachment[], comparer: Comparer<TItem> = defaultComparer) => {
    items.forEach(item => {
        const originName = item.attributes.Name;
        let index = 1;
        while (attachments.find(_ => comparer(item, _))) {
            const { name, extension } = extractNameAndExtension(originName);
            item.attributes.Name = `${name}_${index}${extension ? `.${extension}` : ""}`;
            ++index;
        }
    });

    return items;
}

function extractNameAndExtension(fullName?: string): { name: string; extension: string } {
    if (!fullName) {
        return { name: "", extension: "" };
    }

    const lastDotIndex = fullName.lastIndexOf(".");

    if (lastDotIndex === -1) {
        // No extension found
        return { name: fullName, extension: "" };
    }

    const name = fullName.substring(0, lastDotIndex);
    const extension = fullName.substring(lastDotIndex + 1);

    return { name, extension };
}

export { ReplaceDialog, useActionsWithReplace }